import {
	useState,
	useEffect,
	useCallback,
	useMemo,
} from "react";
import { useEffectEvent } from "./use-effect-event";
import { TableColumn } from "@/components/table/data-table";

interface ColumnVisibilityState {
	visibleColumns: string[];
	toggleColumn: (columnKey: string) => void;
	setColumnVisible: (columnKey: string, visible: boolean) => void;
	resetToDefaults: () => void;
	isColumnVisible: (columnKey: string) => boolean;
	getVisibleColumns: () => TableColumn[];
	allColumns: TableColumn[];
}

/**
 * Modo "server-side". Cuando se pasa `externalVisible`, el hook deja
 * de usar `localStorage` y de mantener state local: la fuente de
 * verdad es lo que venga de afuera (p. ej. el resultado de un
 * `useQuery` Convex). `onVisibilityChange` recibe la lista resuelta
 * para que el caller la persista (generalmente llamando a una
 * mutation server-side con optimistic update).
 *
 * `hasAny` distingue "el user no ha decidido todavía" del "el
 * user eligió no ver nada". Si el caller no conoce `hasAny` (lo
 * pasa `undefined` o no setea el campo), el hook se comporta
 * como siempre: si `visible` es `[]` con `hasAny: true`, respeta
 * la decisión del user; con `hasAny: false` o ausente, cae al
 * default `hiddenByDefault`.
 *
 * Mientras `externalVisible` esté `undefined` (primera carga,
 * network lento), el hook cae al comportamiento histórico:
 * `localStorage` + `hiddenByDefault`. Esto evita el flash de
 * "todo oculto" entre el mount y la primera respuesta del query.
 */
export interface ExternalColumnVisibility {
	externalVisible?: {
		visible: ReadonlyArray<string>;
		hasAny?: boolean;
	};
	onVisibilityChange?: (nextVisible: string[]) => void;
}

/**
 * Set de columnas que, declarativamente, el factory pidió que
 * arranquen OCULTAS. Sirve para columnas virtuales (ej. UUID)
 * que no son parte del set configurable del usuario pero
 * aparecen en la tabla. El usuario puede activarlas desde el
 * popover sin tener que pedirle al factory un nuevo flag.
 */
export type HiddenByDefault = ReadonlySet<string>;

/**
 * Override reactivo de visibilidad por columna, en orden de
 * precedencia sobre `localStorage` y `hiddenByDefault`. Lo usa
 * la tabla de `collection_records`: el server es la fuente de
 * verdad para los system fields virtuales (`_id`, `_uuid`),
 * así que cuando el chip del FieldsPanel invierte la
 * visibilidad, la tabla se re-hidrata al toque sin esperar al
 * storage event. Solo las claves presentes en el mapa se
 * fuerzan; las que no aparecen caen al comportamiento normal
 * (`localStorage` → `hiddenByDefault`).
 *
 * @example
 *   useColumnVisibility(id, cols, defaults, { _uuid: true })
 */
export type ColumnVisibilityOverride = Readonly<Record<string, boolean>>;

export function useColumnVisibility(
	tableId: string,
	columns: TableColumn[],
	hiddenByDefault?: HiddenByDefault,
	columnVisibilityOverride?: ColumnVisibilityOverride,
	external?: ExternalColumnVisibility,
): ColumnVisibilityState {
	const externalVisible = external?.externalVisible;
	const onVisibilityChange = external?.onVisibilityChange;
	// ✅ OPTIMIZACIÓN 1: Memoizar storageKey (solo si vamos a usarlo).
	const storageKey = useMemo(
		() => (externalVisible === undefined ? `table-columns-${tableId}` : null),
		[tableId, externalVisible],
	);
	// Columnas que el usuario ya "vio" alguna vez. Una columna que no está acá
	// es nueva (p. ej. un campo personalizado recién agregado en Colecciones) y
	// arranca visible aunque el usuario tenga una selección guardada; las que
	// ocultó a propósito siguen ocultas.
	const knownStorageKey = useMemo(
		() => (externalVisible === undefined ? `table-columns-known-${tableId}` : null),
		[tableId, externalVisible],
	);
	const isExternalMode = externalVisible !== undefined;

	// ✅ OPTIMIZACIÓN 2: Memoizar requiredColumns
	const requiredColumns = useMemo(
		() =>
			new Set(
				columns
					.filter((col) => col.required === true)
					.map((col) => col.key),
			),
		[columns],
	);

	// ✅ OPTIMIZACIÓN 3: Memoizar allColumnKeys (por contenido: el array de
	// columnas se recrea en cada render del factory).
	const columnKeysSignature = columns.map((col) => col.key).join("|");
	const allColumnKeys = useMemo(
		() => (columnKeysSignature ? columnKeysSignature.split("|") : []),
		[columnKeysSignature],
	);

	// ✅ OPTIMIZACIÓN 4: Función helper pura (no useEffectEvent)
	const ensureMinimumColumns = useCallback(
		(visibleSet: Set<string>) => {
			// Asegurar columnas obligatorias
			requiredColumns.forEach((colKey) => visibleSet.add(colKey));

			// Asegurar al menos una columna visible
			if (visibleSet.size === 0) {
				const firstAvailable =
					columns.find((col) => !requiredColumns.has(col.key)) ||
					columns[0];
				if (firstAvailable) {
					visibleSet.add(firstAvailable.key);
				}
			}
		},
		[requiredColumns, columns],
	);

	// ✅ OPTIMIZACIÓN 5: useEffectEvent solo para localStorage (side effect)
	const persistVisibility = useEffectEvent((newVisibleColumns: string[]) => {
		if (!storageKey) return;
		try {
			localStorage.setItem(storageKey, JSON.stringify(newVisibleColumns));
		} catch (error) {
			console.warn("Error saving column visibility to localStorage:", error);
		}
	});

	// En modo server-side, el "toggle" no escribe localStorage: notifica al
	// caller para que persista con su propia mutation. Devolvemos el callback
	// inmutable para no romper la firma de los handlers.
	const emitExternalChange = useCallback(
		(next: string[]) => {
			onVisibilityChange?.(next);
		},
		[onVisibilityChange],
	);

	// Estado inicial
	const getInitialVisibleColumns = useCallback((): string[] => {
		try {
			if (!storageKey) {
				// Modo server-side sin storage: respetamos
				// `hiddenByDefault` para no flashear "todo
				// visible" antes de que el query del server
				// responda. El effect posterior (cuando
				// `externalVisible` resuelve) pisa esto con la
				// fuente de verdad final.
				if (hiddenByDefault && hiddenByDefault.size > 0) {
					return allColumnKeys.filter((k) => !hiddenByDefault.has(k));
				}
				return allColumnKeys;
			}
			const stored = localStorage.getItem(storageKey);
			if (stored) {
				const parsedVisible = JSON.parse(stored) as string[];
				const visibleSet = new Set(parsedVisible);

				// `explicitEmpty`: el storage tiene `"[]"` (literal),
				// lo que significa "no quiero ver NINGUNA columna".
				// Sin este flag no podemos distinguir eso del
				// caso "todavia no he decidido nada" (storage
				// inexistente, primera visita) — el fallback
				// `visibleSet.size === 0 -> allColumnKeys`
				// confundiría los dos. Las system fields (`_id`,
				// `_uuid`) son las mas afectadas porque arrancan
				// ocultas por `hiddenByDefault`, asi que un
				// usuario que oculte las dos desde el toggle del
				// FieldsPanel termina con `storage = "[]"`.
				const explicitEmpty =
					Array.isArray(parsedVisible) && parsedVisible.length === 0;

				// Columnas nuevas (nunca vistas): visibles por defecto,
				// a menos que estén en `hiddenByDefault`.
				const knownRaw = knownStorageKey
					? localStorage.getItem(knownStorageKey)
					: null;
				if (knownRaw) {
					const known = new Set(JSON.parse(knownRaw) as string[]);
					allColumnKeys.forEach((colKey) => {
						if (!known.has(colKey) && !hiddenByDefault?.has(colKey))
							visibleSet.add(colKey);
					});
				} else {
					// Primera vez que se carga la tabla (sin storage
					// histórico): aplicar `hiddenByDefault` de entrada
					// para que la columna uuid arranque oculta.
					allColumnKeys.forEach((colKey) => {
						if (hiddenByDefault?.has(colKey)) visibleSet.delete(colKey);
					});
				}

				// Asegurar columnas obligatorias
				requiredColumns.forEach((colKey) => visibleSet.add(colKey));

				// Fallback: si el set quedo vacio pero el storage
				// NO era `[]` (sea por que ninguna columna sobrevivio
				// al filtro, o por que el storage era invalido),
				// mostrar todas como en la primera visita. Si el
				// usuario guardo `[]` explicitamente, respetamos
				// la decision (no le forzamos columnas encima).
				// Solo columnas que existen hoy: el storage puede traer keys de
				// campos borrados o renombrados (inflaban el "Columnas (x/y)").
				const current = allColumnKeys.filter((k) => visibleSet.has(k));
				if (current.length === 0 && !explicitEmpty) {
					return allColumnKeys;
				}

				return current;
			}
		} catch (error) {
			console.warn(
				"Error loading column visibility from localStorage:",
				error,
			);
		}

		// Sin storage: respetar `hiddenByDefault`.
		if (hiddenByDefault && hiddenByDefault.size > 0) {
			return allColumnKeys.filter((k) => !hiddenByDefault.has(k));
		}
		return allColumnKeys;
	}, [storageKey, knownStorageKey, allColumnKeys, requiredColumns, hiddenByDefault]);

	/**
	 * Aplica el `columnVisibilityOverride` (server como fuente de
	 * verdad para los system fields virtuales) sobre el set
	 * resuelto por `getInitialVisibleColumns`. Solo las claves
	 * presentes en el override se fuerzan; el resto del set
	 * respeta `localStorage` / `hiddenByDefault` como hasta ahora.
	 */
	const applyOverride = useCallback(
		(baseVisible: string[]): string[] => {
			if (!columnVisibilityOverride) return baseVisible;
			const set = new Set(baseVisible);
			for (const colKey of allColumnKeys) {
				const forced = columnVisibilityOverride[colKey];
				if (typeof forced !== "boolean") continue;
				if (forced) set.add(colKey);
				else set.delete(colKey);
			}
			// Mismas invariantes que `getInitialVisibleColumns`:
			// re-asegurar obligatorias y al menos una visible.
			const ensured = new Set(set);
			requiredColumns.forEach((key) => ensured.add(key));
			if (ensured.size === 0) {
				const first = allColumnKeys[0];
				if (first) ensured.add(first);
			}
			return Array.from(ensured);
		},
		[columnVisibilityOverride, allColumnKeys, requiredColumns],
	);

	const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
		applyOverride(getInitialVisibleColumns()),
	);

	// En modo server-side, `externalVisible` reemplaza al state local.
	// Usamos un effect para mantener `visibleColumns` sincronizado con la
	// fuente externa cuando cambia, evitando un useState derivado dentro del
	// render (que produciría renders extra y posibles loops).
	useEffect(() => {
		if (!isExternalMode || externalVisible === undefined) return;
		// Sin decisión previa del user (server sin filas): caemos
		// al default `hiddenByDefault` para no arrancar con todo
		// oculto ni con todo visible.
		if (externalVisible.hasAny !== true) {
			const seed = hiddenByDefault
				? allColumnKeys.filter((k) => !hiddenByDefault.has(k))
				: [...allColumnKeys];
			// Asegurar columnas obligatorias y al menos una visible
			// (mismas invariantes que `getInitialVisibleColumns`).
			const ensured = new Set(seed);
			requiredColumns.forEach((k) => ensured.add(k));
			if (ensured.size === 0) {
				const first = allColumnKeys[0];
				if (first) ensured.add(first);
			}
			setVisibleColumns(Array.from(ensured));
			return;
		}
		// El user ya decidió (server devolvió filas, pueden haber
		// sido filtradas por admin y quedar en `[]`). Respetamos
		// la decisión.
		const externalSet = new Set(externalVisible.visible);
		requiredColumns.forEach((k) => externalSet.add(k));
		const next = allColumnKeys.filter((k) => externalSet.has(k));
		setVisibleColumns(next);
	}, [externalVisible, allColumnKeys, isExternalMode, hiddenByDefault, requiredColumns]);

	// Actualizar cuando cambien las columnas (modo localStorage únicamente).
	useEffect(() => {
		if (isExternalMode) return;
		setVisibleColumns(applyOverride(getInitialVisibleColumns()));
	}, [getInitialVisibleColumns, applyOverride, isExternalMode]);

	// Escuchar cambios en `localStorage` hechos desde OTRO
	// componente de la misma SPA (p. ej. el chip "mostrar en
	// tabla" de los system fields en el FieldsPanel). El hook
	// mantiene su state local, pero si otro punto de la app
	// modifica la misma key, queremos re-leerlo al instante
	// para que la tabla refleje el cambio sin un refresh.
	useEffect(() => {
		if (isExternalMode || typeof window === "undefined" || !storageKey) return;
		const handleStorage = (e: StorageEvent) => {
			if (e.key !== storageKey) return;
			setVisibleColumns(applyOverride(getInitialVisibleColumns()));
		};
		window.addEventListener("storage", handleStorage);
		return () => window.removeEventListener("storage", handleStorage);
	}, [storageKey, getInitialVisibleColumns, applyOverride, isExternalMode]);

	// Registrar las columnas actuales como conocidas (unión: nunca se quitan,
	// así una columna que tarda en cargar no "renace" como nueva). Va DESPUÉS
	// del efecto de arriba para que este vea las nuevas como no conocidas.
	useEffect(() => {
		if (isExternalMode || !knownStorageKey) return;
		try {
			const knownRaw = localStorage.getItem(knownStorageKey);
			const known = new Set(
				knownRaw ? (JSON.parse(knownRaw) as string[]) : [],
			);
			const before = known.size;
			allColumnKeys.forEach((colKey) => known.add(colKey));
			if (!knownRaw || known.size !== before) {
				localStorage.setItem(
					knownStorageKey,
					JSON.stringify(Array.from(known)),
				);
			}
		} catch {
			// localStorage no disponible: sin memoria de columnas conocidas.
		}
	}, [knownStorageKey, allColumnKeys, isExternalMode]);

	// ✅ OPTIMIZACIÓN 6: Toggle optimizado
	const toggleColumn = useCallback(
		(columnKey: string) => {
			// En modo server-side mantenemos state local optimistic
			// (mismo comportamiento que localStorage) Y emitimos el
			// siguiente set al caller para que persista. La fuente
			// final sigue siendo el server: cuando el query se
			// revalida, el effect que mira `externalVisible` pisa
			// cualquier divergencia local con la respuesta real.
			if (isExternalMode) {
				if (requiredColumns.has(columnKey)) return;
				// Calculamos el siguiente set fuera del updater para
				// evitar doble-fire en StrictMode (React invocaría
				// el updater dos veces). Solo emitimos una vez.
				const set = new Set(visibleColumns);
				if (set.has(columnKey)) set.delete(columnKey);
				else set.add(columnKey);
				const next = allColumnKeys.filter((k) => set.has(k));
				setVisibleColumns(next);
				emitExternalChange(next);
				return;
			}
			setVisibleColumns((prev) => {
				// No permitir ocultar columnas obligatorias
				if (requiredColumns.has(columnKey)) {
					console.log("⚠️ Cannot hide required column:", columnKey);
					return prev;
				}

				const visibleSet = new Set(prev);

				if (visibleSet.has(columnKey)) {
					visibleSet.delete(columnKey);
					console.log("👁️ Hiding column:", columnKey);
				} else {
					visibleSet.add(columnKey);
					console.log("👁️ Showing column:", columnKey);
				}

				// ✅ Llamar función helper pura (no useEffectEvent)
				ensureMinimumColumns(visibleSet);

				const newVisible = Array.from(visibleSet);

				// ✅ persistVisibility se ejecutará después del render
				return newVisible;
			});
		},
		[
			requiredColumns,
			ensureMinimumColumns,
			isExternalMode,
			visibleColumns,
			allColumnKeys,
			emitExternalChange,
		],
	);

	// ✅ OPTIMIZACIÓN 7: Efecto para persistir después del cambio
	useEffect(() => {
		if (isExternalMode) return;
		persistVisibility(visibleColumns);
	}, [visibleColumns, persistVisibility, isExternalMode]);

	// ✅ OPTIMIZACIÓN 8: setColumnVisible optimizado
	const setColumnVisible = useCallback(
		(columnKey: string, visible: boolean) => {
			if (isExternalMode) {
				if (!visible && requiredColumns.has(columnKey)) return;
				const set = new Set(visibleColumns);
				if (visible) set.add(columnKey);
				else set.delete(columnKey);
				const next = allColumnKeys.filter((k) => set.has(k));
				setVisibleColumns(next);
				emitExternalChange(next);
				return;
			}
			setVisibleColumns((prev) => {
				// No permitir ocultar columnas obligatorias
				if (!visible && requiredColumns.has(columnKey)) {
					return prev;
				}

				const visibleSet = new Set(prev);

				if (visible) {
					visibleSet.add(columnKey);
				} else {
					visibleSet.delete(columnKey);
				}

				ensureMinimumColumns(visibleSet);

				return Array.from(visibleSet);
			});
		},
		[
			requiredColumns,
			ensureMinimumColumns,
			isExternalMode,
			visibleColumns,
			allColumnKeys,
			emitExternalChange,
		],
	);

	// ✅ OPTIMIZACIÓN 9: resetToDefaults optimizado
	const resetToDefaults = useCallback(() => {
		if (isExternalMode) {
			setVisibleColumns(allColumnKeys);
			emitExternalChange(allColumnKeys);
			return;
		}
		setVisibleColumns(allColumnKeys);
	}, [allColumnKeys, isExternalMode, emitExternalChange]);

	// ✅ OPTIMIZACIÓN 10: isColumnVisible con Set para O(1)
	const visibleColumnsSet = useMemo(
		() => new Set(visibleColumns),
		[visibleColumns],
	);

	const isColumnVisible = useCallback(
		(columnKey: string): boolean => {
			return visibleColumnsSet.has(columnKey);
		},
		[visibleColumnsSet],
	);

	// ✅ OPTIMIZACIÓN 11: getVisibleColumns memoizado
	const getVisibleColumns = useCallback((): TableColumn[] => {
		return columns.filter((col) => visibleColumnsSet.has(col.key));
	}, [columns, visibleColumnsSet]);

	return {
		visibleColumns,
		toggleColumn,
		setColumnVisible,
		resetToDefaults,
		isColumnVisible,
		getVisibleColumns,
		allColumns: columns,
	};
}

export default useColumnVisibility;
